import { useMutation } from '@apollo/client/react';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { CircularProgress, Grid, IconButton, Tooltip } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import credentials from '../../../../../components/common/credential';
import Map from '../../../../../components/common/Map';
import { CustomDialog } from '../../../../../components/customDialog/CustomDialog';
import { TextInput } from '../../../../../components/textInput/TextInput';
import { SAVE_EVALUACION_DIRECCION_PACIENTE } from '../../../../../data/graphQL/mutations/GestionAsistencial/saveEvaluacionDireccionPaciente';
import { useDebounce } from '../../../../../hooks/useDebounce';
import { IndicadorEstado } from '../../IndicadorEstado/IndicadorEstado';
import '../../IndicadorEstado/indicadorEstado.scss';


export const GeoPosicionamiento = ({
    idEvaluacion,
    direccionPaciente,
    handleChange,
    cambioDireccion,
    setCambioDireccion,
    locationIcon,
    onlyView
}) => {

    const mapURL = `https://maps.googleapis.com/maps/api/js?v=3.exp&key=${credentials.mapsKey}`;

    const [openModal, setOpenModal] = useState(false);
    const [latitud, setLatitud] = useState(direccionPaciente?.latitud ?? 0);
    const [longitud, setLongitud] = useState(direccionPaciente?.longitud ?? 0);
    const [paciente, setPaciente] = useState(direccionPaciente || {});
    const [direccionSAP] = useState(direccionPaciente || {});
    const hasSAPCode = useMemo(() => (!!direccionPaciente?.codigoSAP), [direccionPaciente?.codigoSAP]);
    const direccionCompleta = useMemo(() => (paciente.direccion + ", " + paciente.localidad + ", " + paciente.provincia), [paciente]);
    const handleOpenModal = () => setOpenModal(true);
    const handleCloseModal = () => setOpenModal(false);

    // ref que indica si el usuario modificó manualmente algún campo de dirección
    const hasManualChange = useRef(false);

    const { latitudOriginalPaciente, longitudOriginalPaciente } = useMemo(() => ({
        latitudOriginalPaciente: direccionPaciente?.latitud ?? 0,
        longitudOriginalPaciente: direccionPaciente?.longitud ?? 0
    }), [direccionPaciente]);

    // indica si el paciente ya tiene coordenadas originales cargadas
    const hasOriginalCoordinates = useMemo(() => (
        direccionPaciente?.latitud != null && direccionPaciente?.longitud != null
    ), [direccionPaciente]);

    //resuelve latitud y longitud desde la direccion
    const ubicacion = useMemo(() => (`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(direccionCompleta)}&key=${credentials.mapsKey}`), [direccionCompleta]);
    const debouncedUbicacionValue = useDebounce(ubicacion, 1000);

    useEffect(() => {
        setPaciente({ ...direccionPaciente });
        // al cambiar el paciente, se vuelve a respetar la coordenada original hasta que haya edición manual
        hasManualChange.current = false;
    }, [direccionPaciente]);

    useEffect(() => {
        // si hay coordenadas originales del paciente y todavía no hubo edición manual,
        // se mantiene el punto original y no se recalcula por geocoding
        if (hasOriginalCoordinates && !hasManualChange.current) return;

        if (paciente.direccion !== null && paciente.direccion !== '') {
            fetch(ubicacion)
                .then(response => response.json())
                .then(data => {
                    const status = data.status;

                    if (status === "OK") {
                        let location = data.results[0].geometry.location;
                        setLatitud(location.lat);
                        setLongitud(location.lng);
                    } else {
                        console.error('Geocoding failed:', status);
                    }
                })
                .catch(error => console.error('Error al buscar ubicación:', error));
        }
    }, [debouncedUbicacionValue, hasOriginalCoordinates]);

    const handleChangeProp = (name, value) => {
        hasManualChange.current = true;
        setPaciente(prevDatos => ({
            ...prevDatos,
            [name]: value
        }));
    }

    // se envían a grabar los datos originales de SAP y la latitud y longitud posicionadas.
    // Si se modificó en este popup la dirección para poder geoposicionar correctamente no se almacena dicha dirección,
    // solo se iguala a SAP
    const [saveDireccion, { data: dataSaveUbicacion, error: errorUbicacion, loading: loadingUbicacion }] = useMutation(SAVE_EVALUACION_DIRECCION_PACIENTE, {
        variables: {
            input: {
                idEvaluacion: idEvaluacion,
                direccion: (!!direccionSAP.direccion) ? direccionSAP.direccion : direccionPaciente.direccion,
                localidad: (!!direccionSAP.localidad) ? direccionSAP.localidad : direccionPaciente.localidad,
                provincia: (!!direccionSAP.provincia) ? direccionSAP.provincia : direccionPaciente.provincia,
                codigoPostal: (!!direccionSAP.codigoPostal) ? direccionSAP.codigoPostal : direccionPaciente.codigoPostal,
                latitud: latitud.toString(),
                longitud: longitud.toString()
            }
        }
    });

    useEffect(() => {
        if (!dataSaveUbicacion) return;
        if (dataSaveUbicacion.saveDireccionPacienteFull.success && dataSaveUbicacion.saveDireccionPacienteFull.valorEsperado) {
            handleChange('latitud', latitud);
            handleChange('longitud', longitud);
            setCambioDireccion(false);
            handleCloseModal();
        }
        else {
            toast.error("No fue posible actualizar la Geoposición del paciente.");
        }
    }, [dataSaveUbicacion]);

    useEffect((errorUbicacion) => {
        if (!errorUbicacion) return;
        toast.error("No fue posible actualizar la Geoposición del paciente.");
    }, [errorUbicacion]);

    const handleAccept = () => {
        saveDireccion();
    }

    const states = useMemo(() => ({
        success: hasSAPCode && !cambioDireccion,
        error: !hasSAPCode || cambioDireccion
    }), [hasSAPCode, cambioDireccion]);

    const stateType = useMemo(() => {
        if (states.success) return 'success';
        if (states.error) return 'error';
        return 'empty';
    }, [states]);

    const title = useMemo(() => {
        if (cambioDireccion) return 'Debe validar nuevamente geoposición';
        if (!hasSAPCode) return 'Debe tener un paciente vinculado a SAP';
    }, [states]);

    return (<>
        <Grid size={{ xs: 20, sm: 10, md: 6, lg: 6 }}>
            {(!locationIcon && states.error) && <Tooltip title={title} placement="top">
                <div className='indicador'>
                    <IndicadorEstado
                        type={stateType}
                        onClick={() => hasSAPCode ? handleOpenModal() : null}
                    />
                </div>
            </Tooltip>}
            {(!locationIcon && !states.error) && <IndicadorEstado
                type={stateType}
                onClick={() => hasSAPCode ? handleOpenModal() : null}
            />}
            {locationIcon && <IconButton onClick={handleOpenModal}>
                <LocationOnOutlinedIcon sx={{ color: (cambioDireccion) ? 'red' : 'green' }} />
            </IconButton>
            }
        </Grid>
        <CustomDialog
            maxWidth="md"
            fullWidth={true}
            open={openModal}
            title={'Geo Posicionamiento de Direcciones'}
            onCancel={handleCloseModal}
            onAccept={!onlyView ? handleAccept : null}
            acceptLoading={loadingUbicacion}
            acceptText='Aplicar'
            cancelText='Cancelar'
        >
            <div className='dialog-content'>
                <Grid
                    container
                    justifyContent={'space-between'}
                    columns={{
                        xs: 1,
                        md: 3
                    }}
                    rowGap={1}
                    spacing={2}
                >
                    <Grid size={{ xs: 1, md: 1 }}>
                        <TextInput
                            label='Dirección'
                            placeholder='Dirección'
                            name='direccion'
                            onChange={(e) => handleChangeProp(e.target.name, e.target.value)}
                            disabled={onlyView}
                            value={paciente?.direccion}
                        />
                    </Grid>
                    <Grid size={{ xs: 1, md: 1 }}>
                        <TextInput
                            label='Localidad'
                            placeholder='Localidad'
                            name='localidad'
                            value={paciente?.localidad}
                            onChange={(e) => handleChangeProp(e.target.name, e.target.value)}
                            disabled={onlyView}
                        />
                    </Grid>
                    <Grid size={{ xs: 1, md: 1 }}>
                        <TextInput
                            label='Provincia'
                            placeholder='Provincia'
                            name='provincia'
                            onChange={(e) => handleChangeProp(e.target.name, e.target.value)}
                            disabled={onlyView}
                            value={paciente?.provincia}
                        />
                    </Grid>
                    <Grid size={{ xs: 1, md: 3 }}>
                        <Map
                            key={`${paciente?.nombre + latitud + longitud}`}
                            googleMapURL={mapURL}
                            containerElement={<div style={{ height: '400px' }} />}
                            mapElement={<div style={{ height: '100%' }} />}
                            loadingElement={<CircularProgress disableShrink />}
                            latitud={latitud}
                            longitud={longitud}
                            usuario={!locationIcon ? paciente?.nombre : null}
                        />
                    </Grid>
                </Grid>
            </div>
        </CustomDialog>
    </>
    )
}